import type { RirekishoData } from '@src/api/core/api-types';
import { API_ENDPOINTS, MOCK_TOKENS } from '@src/constants/apiEndpoints';
import { QUERY_KEYS } from '@src/constants/query_keys';
import { TIMING } from '@src/constants/timing';
import { DATA_EXPORT_VERSION } from '@src/constants/version';
import { errorLogger } from '@src/services/errorLogger';
import {
  updatePdfAccessToken,
  updateRirekishoPdfAccessUrl,
} from '@src/store/slices/unified/pdf_access_url';
import {
  getAllErrorMessages,
  getErrorsByField,
  hasMultipleErrors,
} from '@src/types/shared/api/common';
import type { GeneratePdfApiParams } from '@src/types/shared/api/pdf_api_params';
import type {
  PdfGenerateApiResponse,
  PdfGenerateSuccessResponse,
} from '@src/types/shared/api/pdf_api_response';
import { isPdfGenerateSuccess } from '@src/types/shared/api/pdf_api_response';
import { buildPdfGenerationRequest } from './pdfRequestMapper';

export type PdfDocumentType = 'resume' | 'rirekisho';

export type PdfGenerationParamsMap = {
  resume: GeneratePdfApiParams;
  rirekisho: RirekishoData;
};

type ParseResult = { token: string };

type PdfGenerationConfigEntry<T extends PdfDocumentType> = {
  label: string;
  endpoint: string;
  mockToken: string;
  queryKey: readonly unknown[];
  errorQueryKey: readonly unknown[];
  retryDelayMs?: number;
  buildRequest: (params: PdfGenerationParamsMap[T]) => Record<string, unknown>;
  parseResponse: (response: PdfGenerateApiResponse | RirekishoPdfApiResponse) => ParseResult;
  onSuccessAction: (token: string) => { type: string; payload: string };
  logError?: (error: Error, meta?: Record<string, unknown>) => void;
};

const parseResumeResponse = (
  response: PdfGenerateApiResponse | RirekishoPdfApiResponse
): ParseResult => {
  if (!isPdfGenerateSuccess(response)) {
    const isMultiple = hasMultipleErrors(response);
    const allMessages = getAllErrorMessages(response);
    const errorsByField = getErrorsByField(response);

    console.error('[PDF生成API エラーレスポンス]', {
      code: response.error.code,
      message: response.error.message,
      details: response.error.details,
      timestamp: response.timestamp,
      ...(isMultiple && {
        multipleErrors: true,
        errorCount: response.errors?.length,
        allMessages,
        errorsByField,
      }),
    });

    const errorMessage = isMultiple
      ? `${response.error.message}\n${allMessages.slice(1).join('\n')}`
      : response.error.message;

    const error = new Error(errorMessage);
    Object.assign(error, {
      code: response.error.code,
      details: response.error.details,
      timestamp: response.timestamp,
      ...(isMultiple && {
        errors: response.errors,
        errorsByField,
      }),
    });
    throw error;
  }

  const token = (response as PdfGenerateSuccessResponse).data?.token;
  if (!token) {
    throw new Error('PDF生成APIからトークンが返されませんでした');
  }
  return { token };
};

/**
 * 履歴書データを履歴書PDF生成APIのリクエストに変換
 */
const buildRirekishoPdfRequest = (rirekishoData: RirekishoData): Record<string, unknown> => ({
  version: DATA_EXPORT_VERSION,
  // 基本情報
  basicInfo: {
    lastName: rirekishoData.basicInfo?.lastName || '',
    firstName: rirekishoData.basicInfo?.firstName || '',
    lastNameReading: rirekishoData.basicInfo?.lastNameReading || '',
    firstNameReading: rirekishoData.basicInfo?.firstNameReading || '',
    gender: rirekishoData.basicInfo?.gender || 'male',
    birthDate: rirekishoData.basicInfo?.birthDate || '',
    currentDate: rirekishoData.basicInfo?.currentDate || new Date().toISOString().split('T')[0],
    age: rirekishoData.basicInfo?.age || 0,
  },
  // 顔写真情報は削除 - APIで自動的にプロフィール画像が適用される
  // 現住所情報
  currentAddress: {
    zipCode: rirekishoData.currentAddress?.zipCode || '',
    address: rirekishoData.currentAddress?.address || '',
    addressReading: rirekishoData.currentAddress?.addressReading || '',
    areaCode: rirekishoData.currentAddress?.areaCode || '',
    phoneNumber: rirekishoData.currentAddress?.phoneNumber || '',
    callInfo: rirekishoData.currentAddress?.callInfo || '',
  },
  // 連絡先情報
  contact: {
    zipCode: rirekishoData.contact?.zipCode || '',
    contact: rirekishoData.contact?.contact || '',
    contactReading: rirekishoData.contact?.contactReading || '',
    areaCode: rirekishoData.contact?.areaCode || '',
    phoneNumber: rirekishoData.contact?.phoneNumber || '',
    callInfo: rirekishoData.contact?.callInfo || '',
  },
  // 学歴・職歴
  history: (rirekishoData.history || []).map((item) => ({
    year: item.year || '',
    month: item.month || '',
    content: item.content || '',
    type: item.type || 'education',
  })),
  // 免許・資格
  licenses: (rirekishoData.licenses || []).map((item) => ({
    year: item.year || '',
    month: item.month || '',
    name: item.name || '',
  })),
  // 志望動機・特技など
  motivation: {
    body: rirekishoData.motivation?.body || '',
  },
  // 本人希望記入欄
  personalRequests: {
    requests: rirekishoData.personalRequests?.requests || [],
    additionalNote: rirekishoData.personalRequests?.additionalNote || '',
  },
  // 署名（基本情報の氏名を使用）
  signature: {
    signature: `${rirekishoData.basicInfo?.lastName || ''}${rirekishoData.basicInfo?.firstName || ''}`,
  },
  // オプション
  options: {
    insertBlankLine: rirekishoData.options?.insertBlankLine || false,
    showGender: rirekishoData.options?.showGender ?? true,
    showPhoto: rirekishoData.options?.showPhoto ?? true,
  },
});

type RirekishoPdfApiResponse = {
  success: true;
  data: {
    token: string;
    expiresAt: string;
    downloadUrl: string;
    estimatedSizeKB?: number;
  };
  message?: string;
  timestamp: string;
};

const parseRirekishoResponse = (
  response: PdfGenerateApiResponse | RirekishoPdfApiResponse
): ParseResult => {
  if (!response?.success) {
    const errorMessage: string =
      'message' in response && typeof response.message === 'string'
        ? response.message
        : '履歴書PDF生成に失敗しました';
    throw new Error(errorMessage);
  }

  const token = response?.data?.token;
  if (!token) {
    throw new Error('履歴書PDF生成APIからトークンが返されませんでした');
  }

  return { token };
};

export const PDF_GENERATION_CONFIGS: {
  [K in PdfDocumentType]: PdfGenerationConfigEntry<K>;
} = {
  resume: {
    label: '職務経歴書',
    endpoint: API_ENDPOINTS.PDF.GENERATE_RESUME,
    mockToken: MOCK_TOKENS.RESUME,
    queryKey: QUERY_KEYS.PDF.LATEST,
    errorQueryKey: QUERY_KEYS.PDF.ERROR,
    retryDelayMs: TIMING.PDF_GENERATION_TIMEOUT,
    buildRequest: buildPdfGenerationRequest,
    parseResponse: parseResumeResponse,
    onSuccessAction: (token) => updatePdfAccessToken(token),
  },
  rirekisho: {
    label: '履歴書',
    endpoint: API_ENDPOINTS.PDF.GENERATE_RIREKISHO,
    mockToken: MOCK_TOKENS.RIREKISHO,
    queryKey: [...QUERY_KEYS.PDF.LATEST, 'rirekisho'],
    errorQueryKey: [...QUERY_KEYS.PDF.ERROR, 'rirekisho'],
    buildRequest: buildRirekishoPdfRequest,
    parseResponse: parseRirekishoResponse,
    onSuccessAction: (token) => updateRirekishoPdfAccessUrl(token),
    logError: (error, meta) => errorLogger.logError(error, meta),
  },
};
